import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store";
import { setSnackbar, SnackbarType } from "store/commonSlice";
import { showSnackbar } from "store/snackbarSlice";
import { IconWarning } from "icons";
import { IPaymentActionItem } from "api/payment/payment.type";
import { ApiResponse } from "api/HttpClient";
import { reasonCodeText } from "utils/reasonError";
import { recordCheckoutAttribution } from "utils/purchaseAttribution";
import { formatDate, DateFormat } from "utils";

export interface PaymentActionOptions {
  // GA4 purchase attribution only (utils/purchaseAttribution). The Stripe price id of the plan
  // being bought, resolved to a package on the success page. On a `checkout` redirect it is
  // stashed; when omitted, a stale value from an abandoned checkout is cleared instead.
  purchasePriceId?: string;
}

export interface PaymentActionResult {
  // true when the handler navigated away (Stripe checkout/portal) — callers can stop spinners.
  redirected: boolean;
  // true when the HttpClient interceptor already handled the response (401/403/409/503) and ran its
  // own UX — callers should treat it as a terminal no-op, not an error.
  handled?: boolean;
  item?: IPaymentActionItem;
}

// Prefer a localized message keyed off the BE `reason_code` (stable, locale-independent string —
// distinct from the numeric `error_code`). Fall back to the verbatim BE `message`, then a generic
// string. Resolves through the shared `reasonCodeText` so the `errors:<code>` rule matches the
// teams error path. Module-scope + (item, t) args so the hook's useCallback identity stays stable.
const resolvePaymentMessage = (item: IPaymentActionItem, t: (key: string) => string): string => reasonCodeText(item.reason_code, item.message || t("billing:payment_action.generic_error"));

// STRIPE P5 / PD5 (v3 P5-B5): a plan change, seat change or new-plan checkout is refused while the buyer's plan is
// past_due — in-envelope `{action: "error", error_code: 409, reason_code: "subscription_past_due"}`. The outstanding
// payment is settled first, in the Billing Portal.
export const SUBSCRIPTION_PAST_DUE_REASON = "subscription_past_due";

export const isPastDueRefusal = (item: IPaymentActionItem | null | undefined): boolean => item?.reason_code === SUBSCRIPTION_PAST_DUE_REASON;

// The seat modal's inline "remove members before reducing seats" answer: v3 `CheckoutRefusal.SEATS_BELOW_ACTIVE_USAGE`
// (409 + `seat_used`). Matched POSITIVELY on its code (R-P5-71) — the past_due and scheduled-change refusals are in-envelope
// 409s too, and a 409 reason added later must not read as "remove members first" either.
// STRIPE P5 / PD1 (v3 P5-B1): a plan / seat change while another is already scheduled for the period end —
// in-envelope 409 with `scheduled_at`. Changed or cancelled in the Billing Portal (Manage billing).
export const CHANGE_ALREADY_SCHEDULED_REASON = "change_already_scheduled";

export const SEATS_BELOW_ACTIVE_USAGE_REASON = "seats_below_active_usage";

export const isScheduledChangeRefusal = (item: { reason_code?: string } | null | undefined): boolean => item?.reason_code === CHANGE_ALREADY_SCHEDULED_REASON;

export const isRemoveMembersFirst = (item: IPaymentActionItem | null | undefined): boolean =>
  !!item && item.action === "error" && item.error_code === 409 && item.reason_code === SEATS_BELOW_ACTIVE_USAGE_REASON;

// REF-5549 (BE REF-5446): /payments/start and /payments/seats always return HTTP 200 with a LIST
// of action items. This hook centralizes the branch so all four startPayment call sites + the
// seat-change modal behave identically:
//   checkout | upgrade | seat_change  → redirect to Stripe (payment_link)
//   contact_account_manager           → global AM info modal (via WarningSnackbar)
//   error + subscription_past_due     → the past_due recovery popup, with the plan-change reason (PD5)
//   error + change_already_scheduled  → "a change takes effect on {date} — Manage billing" warning (PD1)
//   blocked | error | (malformed)     → localized errors:<reason_code> (or BE message) in a snackbar
// The seat-change modal intercepts the 409 "remove members first" case (action "error",
// error_code 409, seat_used) BEFORE delegating here — that one needs inline UX, not a snackbar.
const usePaymentActionHandler = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  return useCallback(
    (res: ApiResponse<IPaymentActionItem[]> | null | undefined, options?: PaymentActionOptions): PaymentActionResult => {
      // 401/403/409/503 resolve with _handled:true + responseData:null; the interceptor already ran
      // logout / its own snackbar (CLAUDE.md Trap #1). Don't fire a second generic-error toast on top.
      if (res?._handled) {
        return { redirected: false, handled: true };
      }

      const item = res?.responseData?.[0];

      if (!item) {
        dispatch(setSnackbar({ message: t("billing:payment_action.generic_error"), icon: IconWarning, type: SnackbarType.Warning }));

        return { redirected: false };
      }

      if (isPastDueRefusal(item)) {
        dispatch(showSnackbar({ type: 402, messageType: "popup", popupType: "past_due", popupReason: "plan_change" }));

        return { redirected: false, item };
      }

      if (isScheduledChangeRefusal(item) && item.scheduled_at) {
        const date = formatDate(item.scheduled_at, DateFormat.SHORT);
        dispatch(setSnackbar({ message: t("billing:payment_action.change_already_scheduled", { date }), icon: IconWarning, type: SnackbarType.Warning }));

        return { redirected: false, item };
      }

      switch (item.action) {
        case "checkout":
        case "upgrade":
        case "seat_change":
          if (item.payment_link) {
            window.location.href = item.payment_link;

            // Every checkout entry point that goes through this hook records its attribution (or
            // clears a stale one) here, at the moment it leaves for Stripe — no caller can skip
            // it. A no-op for upgrade / seat_change, which never return to the success page.
            recordCheckoutAttribution({ redirected: true, item }, { priceId: options?.purchasePriceId });

            return { redirected: true, item };
          }

          dispatch(setSnackbar({ message: resolvePaymentMessage(item, t), icon: IconWarning, type: SnackbarType.Warning }));

          return { redirected: false, item };
        case "contact_account_manager":
          dispatch(showSnackbar({ type: 402, messageType: "popup", popupType: "contact_account_manager" }));

          return { redirected: false, item };
        case "blocked":
        case "error":
        default:
          // reason_code → localized errors:<code> when mapped; otherwise the verbatim BE message.
          dispatch(setSnackbar({ message: resolvePaymentMessage(item, t), icon: IconWarning, type: SnackbarType.Warning }));

          return { redirected: false, item };
      }
    },
    [dispatch, t]
  );
};

export default usePaymentActionHandler;
